import { z } from "zod";

export const CURRENT_PATCH_SCHEMA_VERSION = 1;

const pointSchema = z.object({
  x: z.number(),
  y: z.number(),
});

export const cableEndpointSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("port"),
    nodeId: z.string().min(1),
    portId: z.string().min(1),
    plugStyle: z.enum(["standard", "stackable"]).optional(),
  }),
  z.object({
    kind: z.literal("loose"),
    x: z.number(),
    y: z.number(),
    label: z.string().min(1).optional(),
  }),
]);

export const cableAssemblySchema = z.object({
  id: z.string().min(1),
  number: z.number().int().positive(),
  label: z.string().optional(),
  colour: z.string().min(1),
  endpointA: cableEndpointSchema,
  endpointB: cableEndpointSchema,
  prepared: z.boolean().optional(),
  branches: z.array(cableEndpointSchema).max(4).optional(),
  junction: pointSchema.optional(),
});

export const instrumentNodeSchema = z.object({
  kind: z.literal("instrument"),
  id: z.string().min(1),
  definitionId: z.string().min(1),
  displayName: z.string().min(1).optional(),
  position: pointSchema,
  scale: z.number().positive(),
  controlValues: z.record(z.string(), z.number().min(0).max(1)),
  rearPanelVisible: z.boolean().optional(),
  locked: z.boolean().optional(),
});

export const externalEndpointNodeSchema = z.object({
  kind: z.literal("external-endpoint"),
  id: z.string().min(1),
  displayName: z.string().min(1),
  deviceName: z.string().min(1).optional(),
  portName: z.string().min(1).optional(),
  direction: z.enum(["input", "output", "bidirectional"]),
  signalType: z.enum(["audio", "cv", "gate", "clock", "expression", "mixed", "unknown"]),
  connectorType: z.enum(["3.5mm-ts", "6.35mm-ts", "trs", "din-midi", "usb", "other"]).optional(),
  position: pointSchema,
  edgeDock: z.enum(["left", "right", "top", "bottom", "free"]).optional(),
  description: z.string().optional(),
  voltageNotes: z.string().optional(),
  colorHint: z.string().min(1).optional(),
});

export const utilityPortSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  groupId: z.string().min(1),
  position: pointSchema,
  direction: z.enum(["input", "output", "bidirectional"]).optional(),
  signalType: z
    .enum(["audio", "cv", "gate", "clock", "expression", "mixed", "unknown"])
    .default("mixed"),
  connectorType: z.enum(["3.5mm-ts", "6.35mm-ts", "trs", "other"]).optional(),
});

export const utilityControlSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  type: z.enum(["knob", "switch"]),
  position: pointSchema.optional(),
  value: z.number().min(0).max(1),
});

export const utilityNodeSchema = z.object({
  kind: z.literal("utility"),
  id: z.string().min(1),
  utilityType: z.enum([
    "passive-mult",
    "stackable-junction",
    "splitter-cable",
    "inline-attenuator",
    "standalone-attenuator",
    "generic-utility",
  ]),
  displayName: z.string().min(1),
  position: pointSchema,
  scale: z.number().positive(),
  ports: z.array(utilityPortSchema).min(2),
  controls: z.array(utilityControlSchema),
  visualStyle: z.enum(["compact", "module", "inline"]),
  manufacturer: z.string().min(1).optional(),
  model: z.string().min(1).optional(),
  userNotes: z.string().optional(),
});

export const workspaceNodeSchema = z.discriminatedUnion("kind", [
  instrumentNodeSchema,
  externalEndpointNodeSchema,
  utilityNodeSchema,
]);

const patchMetadataSchema = z.object({
  category: z.string().min(1).optional(),
  tags: z.array(z.string().min(1)),
  favourite: z.boolean(),
  description: z.string(),
  tempo: z.number().positive().optional(),
});

const printSettingsSchema = z.object({
  diagramStyle: z.literal("wireframe"),
  palette: z.enum(["grayscale", "monochrome"]),
  paper: z.enum(["a4", "letter"]),
  orientation: z.enum(["landscape", "portrait"]),
  cableIdentification: z.literal("number-and-pattern"),
});

export const attachmentReferenceSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(["photo", "audio"]),
  filename: z.string().min(1),
  mimeType: z.string().min(1),
  size: z.number().int().nonnegative(),
  createdAt: z.string().min(1),
  title: z.string(),
  description: z.string(),
  featured: z.boolean(),
  tags: z.array(z.string().min(1)),
  duration: z.number().nonnegative().optional(),
  rotation: z.number().min(-270).max(270).optional(),
});

export const customSettingSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  value: z.string(),
  category: z.enum(["midi", "clock", "performance", "external", "other"]),
});

export const performanceStateSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  notes: z.string(),
  capturedAt: z.string().min(1),
  controlValues: z.record(z.string(), z.number().min(0).max(1)),
  cableAssemblies: z.array(cableAssemblySchema),
  customSettings: z.array(customSettingSchema),
});

export const annotationTargetSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("patch") }),
  z.object({ kind: z.literal("section"), sectionId: z.string().min(1) }),
  z.object({ kind: z.literal("control"), controlId: z.string().min(1) }),
  z.object({ kind: z.literal("port"), portId: z.string().min(1) }),
  z.object({ kind: z.literal("cable"), cableId: z.string().min(1) }),
]);

export const patchAnnotationSchema = z.object({
  id: z.string().min(1),
  text: z.string().min(1),
  target: annotationTargetSchema.default({ kind: "patch" }),
});

export const patchDocumentSchema = z.object({
  schemaVersion: z.literal(CURRENT_PATCH_SCHEMA_VERSION),
  id: z.string().min(1),
  title: z.string().min(1),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
  author: z.string().optional(),
  revision: z.object({
    number: z.number().int().positive(),
    label: z.string().min(1).optional(),
  }),
  workspace: z.object({
    viewport: z
      .object({
        x: z.number(),
        y: z.number(),
        scale: z.number().positive(),
      })
      .optional(),
    nodes: z.array(workspaceNodeSchema).min(1),
    cableAssemblies: z.array(cableAssemblySchema),
    annotations: z.array(patchAnnotationSchema),
  }),
  metadata: patchMetadataSchema,
  notes: z.object({ body: z.string() }),
  customSettings: z.array(customSettingSchema),
  attachments: z.array(attachmentReferenceSchema),
  performanceStates: z.array(performanceStateSchema),
  printSettings: printSettingsSchema,
});

export type PatchDocument = z.infer<typeof patchDocumentSchema>;
export type InstrumentNode = z.infer<typeof instrumentNodeSchema>;
export type ExternalEndpointNode = z.infer<typeof externalEndpointNodeSchema>;
export type UtilityNode = z.infer<typeof utilityNodeSchema>;
export type UtilityPort = z.infer<typeof utilityPortSchema>;
export type WorkspaceNode = z.infer<typeof workspaceNodeSchema>;
export type Cable = z.infer<typeof cableAssemblySchema>;
export type CableEndpoint = z.infer<typeof cableEndpointSchema>;

export function cableEndpoints(cable: Cable): CableEndpoint[] {
  return [cable.endpointA, cable.endpointB, ...(cable.branches ?? [])];
}
export type AttachmentReference = z.infer<typeof attachmentReferenceSchema>;
export type CustomSetting = z.infer<typeof customSettingSchema>;
export type PerformanceState = z.infer<typeof performanceStateSchema>;
export type AnnotationTarget = z.infer<typeof annotationTargetSchema>;
export type PatchAnnotation = z.infer<typeof patchAnnotationSchema>;

export function migratePatchDocument(value: unknown): PatchDocument {
  if (!value || typeof value !== "object") {
    throw new Error("Patch document is not an object.");
  }

  const version = Reflect.get(value, "schemaVersion");
  if (version !== CURRENT_PATCH_SCHEMA_VERSION) {
    throw new Error(`Unsupported patch schema version '${String(version)}'.`);
  }

  return patchDocumentSchema.parse(value);
}

export function clonePatchDocument(document: PatchDocument): PatchDocument {
  return structuredClone(document);
}
